import { useState, type FormEvent } from 'react';
import type { TaskPriority } from '@4zen/api-client';
import { Button, Field, Input, Modal, Select, Textarea } from '@4zen/ui';
import { useToast } from '@/components/Toast';
import { useDebounced } from '@/lib/useDebounced';
import { useClients } from '@/features/clients/queries';
import { useUsers } from '@/features/team/queries';
import { useCreateTask } from './queries';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
  defaultClientId?: string;
  defaultAssigneeId?: string;
}

const REMINDER_PRESETS = [
  { value: '10,5,3,1,0', label: 'За 10, 5, 3, 1 день і в день дедлайну' },
  { value: '3,1,0', label: 'За 3, 1 день і в день дедлайну' },
  { value: '0', label: 'Тільки в день дедлайну' },
  { value: '', label: 'Без нагадувань' },
];

export function TaskComposer({
  open,
  onClose,
  defaultDate,
  defaultClientId,
  defaultAssigneeId,
}: Props) {
  const toast = useToast();
  const createTask = useCreateTask();
  const { data: users } = useUsers();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [estimate, setEstimate] = useState('30');
  const [scheduledFor, setScheduledFor] = useState(defaultDate ?? '');
  const [startTime, setStartTime] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [clientId, setClientId] = useState(defaultClientId ?? '');
  const [clientQuery, setClientQuery] = useState('');
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId ?? '');
  const [reminders, setReminders] = useState(REMINDER_PRESETS[0].value);
  const [isFixedTime, setIsFixedTime] = useState(false);
  const [checklist, setChecklist] = useState('');

  const debouncedClientQuery = useDebounced(clientQuery, 250);
  const { data: clients } = useClients({ search: debouncedClientQuery || undefined, limit: 20 });

  // The composer opens from a specific calendar cell; keep that day in sync.
  if (open && defaultDate && !scheduledFor) setScheduledFor(defaultDate);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const day = scheduledFor || null;
    const scheduledStart = day && startTime ? new Date(`${day}T${startTime}`).toISOString() : null;

    try {
      await createTask.mutateAsync({
        title,
        description: description || null,
        priority,
        estimate_minutes: Number(estimate) || 30,
        scheduled_for: day,
        scheduled_start: scheduledStart,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        client_id: clientId || null,
        assignee_id: assigneeId || null,
        is_fixed_time: isFixedTime,
        reminder_offsets_days: reminders ? reminders.split(',').map(Number) : [],
        checklist: checklist
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => ({ title: line })),
      });
      toast.success('Задачу створено');
      setTitle('');
      setDescription('');
      setChecklist('');
      setStartTime('');
      onClose();
    } catch (error) {
      toast.error('Не вдалося створити задачу', (error as Error).message);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Нова задача" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Що потрібно зробити" htmlFor="task-title">
          <Input
            id="task-title"
            required
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>

        <Field label="Деталі" htmlFor="task-description">
          <Textarea
            id="task-description"
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Пріоритет" htmlFor="task-priority">
            <Select
              id="task-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
            >
              <option value="high">Високий</option>
              <option value="medium">Середній</option>
              <option value="low">Низький</option>
            </Select>
          </Field>
          <Field label="Оцінка, хв" htmlFor="task-estimate">
            <Input
              id="task-estimate"
              inputMode="numeric"
              value={estimate}
              onChange={(event) => setEstimate(event.target.value)}
            />
          </Field>
          <Field label="Дедлайн" htmlFor="task-due">
            <Input
              id="task-due"
              type="datetime-local"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="День" htmlFor="task-day">
            <Input
              id="task-day"
              type="date"
              value={scheduledFor}
              onChange={(event) => setScheduledFor(event.target.value)}
            />
          </Field>
          <Field label="Початок" htmlFor="task-start">
            <Input
              id="task-start"
              type="time"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
          </Field>
          <Field label="Виконавець" htmlFor="task-assignee">
            <Select
              id="task-assignee"
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
            >
              <option value="">Я</option>
              {(users?.items ?? []).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.full_name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Клієнт" htmlFor="task-client">
            <Input
              id="task-client"
              placeholder="Почніть вводити назву"
              value={clientQuery}
              onChange={(event) => setClientQuery(event.target.value)}
              list="task-client-options"
            />
            <datalist id="task-client-options">
              {(clients?.items ?? []).map((client) => (
                <option key={client.id} value={client.name} />
              ))}
            </datalist>
            <Select
              className="mt-2"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            >
              <option value="">Без клієнта</option>
              {(clients?.items ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Нагадування" htmlFor="task-reminders">
            <Select
              id="task-reminders"
              value={reminders}
              onChange={(event) => setReminders(event.target.value)}
            >
              {REMINDER_PRESETS.map((preset) => (
                <option key={preset.label} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field
          label="Проміжні етапи"
          htmlFor="task-checklist"
          hint="Один пункт на рядок — вони стануть чек-листом"
        >
          <Textarea
            id="task-checklist"
            rows={3}
            value={checklist}
            onChange={(event) => setChecklist(event.target.value)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input
            type="checkbox"
            checked={isFixedTime}
            onChange={(event) => setIsFixedTime(event.target.checked)}
          />
          Фіксований час (зустріч або дзвінок) — планувальник не пересуватиме
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" loading={createTask.isPending}>
            Створити
          </Button>
        </div>
      </form>
    </Modal>
  );
}
